import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin, Trash2, Eye, Briefcase } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { formatCurrency, formatDate } from "../lib/format";

const STATUS_TONES = {
    confirmed: "bg-blue-50 text-blue-600",
    cancelled: "bg-red-50 text-red-700",
    completed: "bg-navy-50 text-navy-600",
    pending: "bg-cream-200 text-ink-light",
};

const MyBookings = () => {
    const { user, loading: authLoading } = useAuth();
    const { fetchMyBookings, cancelBooking } = useData();
    const [bookings, setBookings] = useState([]);
    const [tab, setTab] = useState("all");
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        try {
            const list = await fetchMyBookings();
            setBookings(list);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load bookings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) refresh();
        else setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const filtered = useMemo(() => {
        if (tab === "all") return bookings;
        return bookings.filter((b) => b.type === tab);
    }, [bookings, tab]);

    const handleCancel = async (id) => {
        if (!window.confirm("Cancel this booking?")) return;
        try {
            await cancelBooking(id);
            toast.success("Booking cancelled.");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not cancel.");
        }
    };

    if (!user && !authLoading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-center">
                <div>
                    <Briefcase className="w-12 h-12 mx-auto text-blue-500" />
                    <h1 className="font-display text-3xl text-ink font-bold mt-3">Sign in to view bookings</h1>
                    <p className="text-ink-light mt-2 text-sm">Track your Kerala trips, download confirmations and cancel bookings — all in one place.</p>
                    <div className="flex items-center justify-center gap-3 mt-5">
                        <Link to="/login" className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold">Login</Link>
                        <Link to="/register" className="px-6 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Sign up</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-cream-100 min-h-screen py-12" data-testid="my-bookings-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <h1 className="font-display text-3xl lg:text-4xl text-ink font-bold tracking-tight">My Bookings</h1>
                <p className="text-ink-light text-sm mt-1">Hello {user?.name} — here's your travel history.</p>

                <div className="mt-6 flex items-center gap-2 border-b border-cream-200">
                    {[
                        { v: "all", l: `All (${bookings.length})` },
                        { v: "cab", l: `Cabs (${bookings.filter((b) => b.type === "cab").length})` },
                        { v: "package", l: `Packages (${bookings.filter((b) => b.type === "package").length})` },
                    ].map((t) => (
                        <button
                            key={t.v}
                            onClick={() => setTab(t.v)}
                            className={`px-4 py-2.5 text-sm font-semibold relative transition ${
                                tab === t.v ? "text-blue-500" : "text-ink-light hover:text-ink"
                            }`}
                            data-testid={`mb-tab-${t.v}`}
                        >
                            {t.l}
                            {tab === t.v && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />}
                        </button>
                    ))}
                </div>

                <div className="mt-6 space-y-4">
                    {loading && <div className="text-center text-ink-light py-10">Loading…</div>}
                    {!loading && filtered.length === 0 && (
                        <div className="bg-white border border-cream-200 rounded-2xl p-12 text-center text-ink-light">
                            No bookings yet. <Link to="/cab-booking" className="text-blue-500 font-semibold">Book a cab</Link> or <Link to="/tour-packages" className="text-blue-500 font-semibold">browse packages</Link>.
                        </div>
                    )}
                    {filtered.map((b) => (
                        <div key={b.id} className="bg-white border border-cream-200 rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-12" data-testid={`booking-${b.id}`}>
                            <img src={b.cabImage || b.packageImage} alt="" className="md:col-span-3 w-full h-40 md:h-full object-cover" />
                            <div className="md:col-span-6 p-5">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-500">{b.type === "cab" ? "Cab Booking" : "Tour Package"}</span>
                                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${STATUS_TONES[b.status] || STATUS_TONES.pending}`}>{b.status}</span>
                                </div>
                                <h3 className="font-display text-xl text-ink font-bold mt-1 leading-tight">{b.cabName || b.packageName}</h3>
                                <p className="text-sm text-ink-light mt-1 flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5 text-blue-500" />
                                    {b.type === "cab" ? `${b.from} → ${b.to}` : `From ${b.startingCity}`}
                                </p>
                                <p className="text-sm text-ink-light mt-1 flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                    {formatDate(b.date || b.startDate)}
                                </p>
                                <div className="text-xs font-mono text-ink-light mt-2">{b.id}</div>
                            </div>
                            <div className="md:col-span-3 p-5 border-t md:border-t-0 md:border-l border-cream-200 flex flex-col items-stretch justify-between gap-3 bg-cream-100/40">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-ink-light font-semibold">Total</div>
                                    <div className="font-display text-xl font-bold text-blue-500">{formatCurrency(b.totalAmount)}</div>
                                    <div className="text-[10px] text-ink-light mt-0.5">Pay direct to driver</div>
                                </div>
                                <div className="space-y-2">
                                    <Link to={`/booking/confirmation/${b.id}`} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold transition">
                                        <Eye className="w-3.5 h-3.5" /> View / Print
                                    </Link>
                                    {b.status !== "cancelled" && (
                                        <button onClick={() => handleCancel(b.id)} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-cream-200 hover:border-red-300 hover:text-red-600 text-ink-light text-xs font-semibold transition" data-testid={`cancel-${b.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" /> Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MyBookings;
